<div class="report-container">
  <!-- Global Loading / Processing Overlay -->
  <div class="modal-overlay overlay-z2000" *ngIf="isLoading || isGeneratingPdf || isExporting">
    <div class="processing-box">
      <div class="spinner spinner-mb"></div>
      <p class="processing-text">{{ loadingMessage }}</p>
    </div>
  </div>

  <div class="header-section">
    <h2>Frame Usage Report</h2>
    <div class="month-label">Report for {{ selectedDate | date:'MMMM yyyy' }}</div>
  </div>

  <div class="filters">
    <div class="filter-group">
      <label>Select Month</label>
      <div class="date-picker-wrapper" style="position: relative; display: flex; align-items: center;">
        <input matInput [value]="selectedDate | date:'MMMM yyyy'" class="date-input" readonly (click)="picker.open()" style="cursor: pointer;">
        <input [matDatepicker]="picker" [value]="selectedDate" style="display: none;">
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker #picker startView="year" (monthSelected)="setMonthAndYear($event, picker)"></mat-datepicker>
      </div>
    </div>
  </div>

  <!-- Top 10 Frames KPI -->
  <div class="kpi-section-label" *ngIf="reportType === 'overall'">Top 10 Frames</div>
  <div class="kpi-container top-frames-container" *ngIf="reportType === 'overall'">
    <div class="top-frame-card" *ngFor="let frame of top10Frames">
      <div class="medal-icon">
        <span *ngIf="frame.rank === 1" class="trophy" title="1st Place">🏆</span>
        <span *ngIf="frame.rank === 2" class="medal silver" title="2nd Place">🥈</span>
        <span *ngIf="frame.rank === 3" class="medal bronze" title="3rd Place">🥉</span>
        <span *ngIf="frame.rank! > 3" class="rank-badge">#{{ frame.rank }}</span>
      </div>
      <div class="frame-image-wrapper" (click)="openPreview(frame)" style="cursor: pointer;" title="Click for details">
        <img *ngIf="frame.imageUrl" [src]="frame.imageUrl" alt="Frame">
        <div class="placeholder-img" *ngIf="!frame.imageUrl">No Img</div>
      </div>
      <div class="frame-info">
        <div class="frame-id" title="{{ frame.frameId }}">{{ frame.frameId }}</div>
        <div class="print-count">{{ frame.printCount | number }} prints</div>
      </div>
    </div>
    <div *ngIf="top10Frames.length === 0 && !isLoading" class="no-data-msg">
      No frames printed for this month.
    </div>
  </div>

  <!-- Summary KPIs (Total Prints & Total Amount) -->
  <div class="summary-kpis-container" *ngIf="!isLoading">
    <div class="summary-kpi-card">
      <div class="summary-kpi-title">Total Prints</div>
      <div class="summary-kpi-value text-blue">{{ totalPrints | number }}</div>
      <div class="summary-kpi-sub">Across all filtered frames</div>
    </div>
    <div class="summary-kpi-card highlight-card">
      <div class="summary-kpi-title">Total Amount (USD)</div>
      <div class="summary-kpi-value text-green">{{ totalAmount | currency:'USD':'symbol':'1.2-2' }}</div>
      <div class="summary-kpi-sub font-mono">Total Prints &times; $0.25</div>
    </div>
  </div>

  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
    <div class="report-tabs" style="margin-bottom: 0;">
      <button [class.active]="reportType === 'overall'" (click)="onReportTypeChange('overall')">Overall</button>
      <button [class.active]="reportType === 'store'" (click)="onReportTypeChange('store')">By Store</button>
      <button [class.active]="reportType === 'device'" (click)="onReportTypeChange('device')">By Device</button>
    </div>

    <div class="filter-group dropdowns-group" style="display: flex; gap: 12px; flex-direction: row; align-items: center;">
      <!-- Frame Group ID Filter -->
      <div class="filter-dropdown" [class.dropdown-active]="showFrameGroupFilterDropdown">
        <button class="dropdown-trigger-btn" (click)="toggleFrameGroupFilter()" [class.active]="showFrameGroupFilterDropdown" [disabled]="allUniqueFrameGroups.length === 0">
          <span>Select Frame Groups ({{ selectedFrameGroups.size }}/{{ allUniqueFrameGroups.length }})</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class.rotated]="showFrameGroupFilterDropdown"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </button>
        <div class="dropdown-menu" *ngIf="showFrameGroupFilterDropdown">
          <div class="dropdown-item" style="padding: 4px 8px; cursor: default;" (click)="$event.stopPropagation()">
            <input type="text" [(ngModel)]="frameGroupFilterSearchText" placeholder="Search frame group..." style="width: 100%; padding: 6px; border: 1px solid #cbd5e1; border-radius: 4px; box-sizing: border-box; font-size: 0.85rem;">
          </div>
          <div class="dropdown-item" (click)="toggleAllFrameGroups()">
            <input type="checkbox" [checked]="isAllFrameGroupsSelected" (click)="$event.stopPropagation(); toggleAllFrameGroups()">
            <span>Select All</span>
          </div>
          <div class="dropdown-divider"></div>
          <div class="dropdown-scroll">
            <div class="dropdown-item" *ngFor="let g of getFilteredFrameGroups()" (click)="toggleFrameGroupSelection(g)">
              <input type="checkbox" [checked]="selectedFrameGroups.has(g)" (click)="$event.stopPropagation(); toggleFrameGroupSelection(g)">
              <span>{{ g }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Store Filter -->
      <div class="filter-dropdown" [class.dropdown-active]="showStoreFilterDropdown" *ngIf="reportType === 'store' || reportType === 'device'">
        <button class="dropdown-trigger-btn" (click)="toggleStoreFilter()" [class.active]="showStoreFilterDropdown" [disabled]="allUniqueStores.length === 0">
          <span>Select Stores ({{ selectedStores.size }}/{{ allUniqueStores.length }})</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class.rotated]="showStoreFilterDropdown"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </button>
        <div class="dropdown-menu" *ngIf="showStoreFilterDropdown">
          <div class="dropdown-item" style="padding: 4px 8px; cursor: default;" (click)="$event.stopPropagation()">
            <input type="text" [(ngModel)]="storeFilterSearchText" placeholder="Search store..." style="width: 100%; padding: 6px; border: 1px solid #cbd5e1; border-radius: 4px; box-sizing: border-box; font-size: 0.85rem;">
          </div>
          <div class="dropdown-item" (click)="toggleAllStores()">
            <input type="checkbox" [checked]="isAllStoresSelected" (click)="$event.stopPropagation(); toggleAllStores()">
            <span>Select All</span>
          </div>
          <div class="dropdown-divider"></div>
          <div class="dropdown-scroll">
            <div class="dropdown-item" *ngFor="let s of getFilteredStores()" (click)="toggleStoreSelection(s)">
              <input type="checkbox" [checked]="selectedStores.has(s)" (click)="$event.stopPropagation(); toggleStoreSelection(s)">
              <span>{{ s }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Device Filter -->
      <div class="filter-dropdown" [class.dropdown-active]="showDeviceFilterDropdown" *ngIf="reportType === 'device'">
        <button class="dropdown-trigger-btn" (click)="toggleDeviceFilter()" [class.active]="showDeviceFilterDropdown" [disabled]="allUniqueDevices.length === 0">
          <span>Select Devices ({{ selectedDevices.size }}/{{ allUniqueDevices.length }})</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class.rotated]="showDeviceFilterDropdown"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </button>
        <div class="dropdown-menu" *ngIf="showDeviceFilterDropdown">
          <div class="dropdown-item" style="padding: 4px 8px; cursor: default;" (click)="$event.stopPropagation()">
            <input type="text" [(ngModel)]="deviceFilterSearchText" placeholder="Search device..." style="width: 100%; padding: 6px; border: 1px solid #cbd5e1; border-radius: 4px; box-sizing: border-box; font-size: 0.85rem;">
          </div>
          <div class="dropdown-item" (click)="toggleAllDevices()">
            <input type="checkbox" [checked]="isAllDevicesSelected" (click)="$event.stopPropagation(); toggleAllDevices()">
            <span>Select All</span>
          </div>
          <div class="dropdown-divider"></div>
          <div class="dropdown-scroll">
            <div class="dropdown-item" *ngFor="let d of getFilteredDevices()" (click)="toggleDeviceSelection(d)">
              <input type="checkbox" [checked]="selectedDevices.has(d)" (click)="$event.stopPropagation(); toggleDeviceSelection(d)">
              <span>{{ d }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Export to PDF Button -->
      <button class="action-btn-pdf" (click)="exportPdf()" [disabled]="isExporting || gridData.length === 0" title="Export filtered report to PDF">
        <div *ngIf="isExporting" class="spinner-sm-inline"></div>
        <svg *ngIf="!isExporting" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
        <span>{{ isExporting ? 'Generating PDF...' : 'Export to PDF' }}</span>
      </button>
    </div>
  </div>

  <div class="table-container">
    <div class="loading-overlay" *ngIf="isLoading">
      <div class="spinner"></div>
    </div>
    <table class="report-table">
      <thead>
        <tr>
          <th style="width: 60px;">Rank</th>
          <th *ngIf="reportType === 'device' || reportType === 'store'">Store ID</th>
          <th *ngIf="reportType === 'device'">Device ID</th>
          <th>Frame Group ID</th>
          <th>Frame ID</th>
          <th>Image</th>
          <th class="number-col">Prints</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of paginatedGridData">
          <td style="font-weight: bold; color: #1e293b;">#{{ row.rank }}</td>
          <td *ngIf="reportType === 'device' || reportType === 'store'">{{ row.storeId }}</td>
          <td *ngIf="reportType === 'device'">{{ row.deviceId }}</td>
          <td>{{ row.frameGroupId }}</td>
          <td><div class="frame-id">{{ row.frameId }}</div></td>
          <td class="image-col">
            <img *ngIf="row.imageUrl" [src]="row.imageUrl" alt="Frame" class="grid-img" (click)="openPreview(row)" style="cursor: pointer;" title="Click for details">
            <span *ngIf="!row.imageUrl" class="no-img">No Img</span>
          </td>
          <td class="number-col font-bold">{{ row.printCount | number }}</td>
        </tr>
        <tr *ngIf="gridData.length === 0 && !isLoading">
          <td colspan="7" style="text-align: center; padding: 20px; color: #666;">No data available.</td>
        </tr>
      </tbody>
    </table>
    <div class="pagination-controls" *ngIf="gridData.length > 0">
      <div class="page-size-selector">
        <label for="pageSize">Rows per page:</label>
        <select id="pageSize" [(ngModel)]="pageSize" (change)="onPageSizeChange()" class="page-select">
          <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }}</option>
        </select>
      </div>
      <div class="page-nav" *ngIf="totalPages > 1">
        <button class="page-btn" (click)="prevPage()" [disabled]="currentPage === 1">Previous</button>
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button class="page-btn" (click)="nextPage()" [disabled]="currentPage === totalPages">Next</button>
      </div>
    </div>
  </div>
</div>

<!-- Full Size Image Preview Modal -->
<div class="modal-overlay" *ngIf="previewFrame" (click)="closePreview()">
  <div class="preview-content" style="display: flex; gap: 20px; background: white; padding: 20px; border-radius: 10px; max-width: 90%; max-height: 90vh;" (click)="$event.stopPropagation()">
    <button class="close-preview-btn" (click)="closePreview()" style="position: absolute; top: 10px; right: 10px; background: transparent; border: none; font-size: 20px; cursor: pointer; color: #64748b;">✖</button>
    <div style="flex: 1; overflow: auto; text-align: center; background-color: #f1f5f9; padding: 20px; border-radius: 8px;">
      <img [src]="previewFrame.imageUrl" alt="Full Size Preview" style="max-height: 80vh; max-width: 100%; object-fit: contain; border-radius: 4px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
    </div>
    <div style="width: 300px; padding-left: 20px; border-left: 1px solid #e2e8f0; text-align: left;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;">
        <h3 style="margin: 0; color: #1e293b; font-size: 1.2rem;">Frame Details</h3>
      </div>

      <div style="margin-bottom: 16px;">
        <span style="font-weight: 600; color: #64748b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em;">Theme</span>
        <div style="color: #0f172a; margin-top: 4px; font-size: 1rem;">{{ previewFrame.themeId }}</div>
      </div>

      <div style="margin-bottom: 16px;">
        <span style="font-weight: 600; color: #64748b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em;">Frame Group</span>
        <div style="color: #0f172a; margin-top: 4px; font-size: 1rem;">{{ previewFrame.frameGroupId }}</div>
      </div>
      
      <div style="margin-bottom: 16px;">
        <span style="font-weight: 600; color: #64748b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em;">Frame ID</span>
        <div style="color: #0f172a; margin-top: 4px; font-size: 1rem; word-break: break-all;">{{ previewFrame.frameId }}</div>
      </div>


      <div style="margin-bottom: 24px;">
        <span style="font-weight: 600; color: #64748b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em;">Total Prints</span>
        <div style="color: #2563eb; font-weight: bold; margin-top: 4px; font-size: 1.5rem;">{{ previewFrame.printCount | number }}</div>
      </div>
      
      <div style="margin-bottom: 16px;">
        <span style="font-weight: 600; color: #64748b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em;">Ranking</span>
        <div style="color: #0f172a; font-weight: bold; margin-top: 4px; font-size: 1.5rem;">#{{ previewFrame.rank }}</div>
      </div>
    </div>
  </div>
</div>

<!-- Profit Report Style Preview Modal (Landscape) -->
<div class="modal-overlay" *ngIf="isPreviewModalOpen" (click)="closePdfPreview()">
  <div class="modal-content large-modal frame-modal-landscape" (click)="$event.stopPropagation()">
    <div class="modal-sticky-header">
      <h2>{{ reportTitle }} Preview</h2>
      <div class="header-actions">
        <button class="action-btn view-btn" (click)="printPreviewReport()" [disabled]="isExporting">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -2px; margin-right: 4px;"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
          Print PDF
        </button>
        <button class="action-btn pdf-btn" (click)="savePreviewReport()" [disabled]="isExporting">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -2px; margin-right: 4px;"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
          Save PDF
        </button>
        <button class="close-btn-static" (click)="closePdfPreview()">&times;</button>
      </div>
    </div>

    <!-- Modal Printable Body & PDF Template (Exact Profit Report styling) -->
    <div id="frame-report-modal-print-content" class="modal-report-printable">
      <!-- Report Header Card -->
      <div class="card header-card">
        <div class="company-info">
          <h2>{{ reportTitle }}</h2>
          <p class="subtle">
            Scope: {{ reportType === 'overall' ? 'Overall Report' : (reportType === 'store' ? 'Store Level Report' : 'Device Level Report') }}
            &bull; Frame Groups: {{ isAllFrameGroupsSelected ? 'All Groups' : (selectedFrameGroups.size + ' Selected') }}
          </p>
        </div>
        <div class="date-info">
          <div class="info-item">
            <label>Statement Period</label>
            <span>{{ selectedDate | date:'MMMM yyyy' }}</span>
          </div>
          <div class="info-item">
            <label>Date Generated</label>
            <span>{{ currentDate | date:'yyyy-MM-dd HH:mm' }}</span>
          </div>
        </div>
      </div>

      <!-- Summary Cards (Profit Report styling) -->
      <div class="summary-grid">
        <div class="card summary-card">
          <label>Total Filtered Frames</label>
          <span class="value">{{ gridData.length | number }}</span>
        </div>
        <div class="card summary-card">
          <label>Total Prints</label>
          <span class="value income">{{ totalPrints | number }}</span>
        </div>
        <div class="card summary-card highlight">
          <label>Total Amount (USD) <span class="rate-note" style="color: #94a3b8;">(Prints &times; $0.25)</span></label>
          <span class="value">{{ totalAmount | currency:'USD':'symbol':'1.2-2' }}</span>
        </div>
      </div>

      <!-- Ranked Table (Profit Report data-table style) -->
      <div class="card main-card" style="padding: 0; overflow: hidden;">
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 50px;">Rank</th>
              <th *ngIf="reportType === 'device' || reportType === 'store'">Store ID</th>
              <th *ngIf="reportType === 'device'">Device ID</th>
              <th>Frame Group ID</th>
              <th>Frame ID</th>
              <th style="width: 70px;">Image</th>
              <th class="text-right" style="width: 100px;">Prints</th>
              <th class="text-right fee-col" style="width: 120px;">Amount ($0.25)</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of gridData">
              <td class="text-center font-bold">#{{ row.rank }}</td>
              <td *ngIf="reportType === 'device' || reportType === 'store'" class="text-center">{{ row.storeId }}</td>
              <td *ngIf="reportType === 'device'" class="text-center">{{ row.deviceId }}</td>
              <td class="text-center">{{ row.frameGroupId }}</td>
              <td class="text-center"><div class="frame-id">{{ row.frameId }}</div></td>
              <td class="text-center image-col">
                <img *ngIf="row.imageUrl" [src]="row.imageUrl" crossorigin="anonymous" referrerpolicy="no-referrer" alt="Frame" class="grid-img">
                <span *ngIf="!row.imageUrl" class="no-img">No Img</span>
              </td>
              <td class="text-right font-bold">{{ row.printCount | number }}</td>
              <td class="text-right font-bold fee-col">{{ ((row.printCount || 0) * 0.25) | currency:'USD':'symbol':'1.2-2' }}</td>
            </tr>
            <tr *ngIf="gridData.length === 0">
              <td colspan="8" class="empty-table-msg">No frame records found for the selected criteria.</td>
            </tr>
          </tbody>
          <tfoot *ngIf="gridData.length > 0">
            <tr>
              <th [attr.colspan]="(reportType === 'device' ? 5 : (reportType === 'store' ? 4 : 3))" class="text-center font-bold">Total</th>
              <th></th>
              <th class="text-right font-bold">{{ totalPrints | number }}</th>
              <th class="text-right font-bold fee-col">{{ totalAmount | currency:'USD':'symbol':'1.2-2' }}</th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</div>

<!-- Dedicated Background PDF Export Template -->
<div class="pdf-export-container">
  <div id="frame-report-pdf-content" class="frame-pdf-wrapper modal-report-printable">
    <div class="card header-card">
      <div class="company-info">
        <h2>{{ reportTitle }}</h2>
        <p class="subtle">
          Scope: {{ reportType === 'overall' ? 'Overall Report' : (reportType === 'store' ? 'Store Level Report' : 'Device Level Report') }}
          &bull; Frame Groups: {{ isAllFrameGroupsSelected ? 'All Groups' : (selectedFrameGroups.size + ' Selected') }}
        </p>
      </div>
      <div class="date-info">
        <div class="info-item">
          <label>Statement Period</label>
          <span>{{ selectedDate | date:'MMMM yyyy' }}</span>
        </div>
        <div class="info-item">
          <label>Date Generated</label>
          <span>{{ currentDate | date:'yyyy-MM-dd HH:mm' }}</span>
        </div>
      </div>
    </div>

    <div class="summary-grid">
      <div class="card summary-card">
        <label>Total Filtered Frames</label>
        <span class="value">{{ gridData.length | number }}</span>
      </div>
      <div class="card summary-card">
        <label>Total Prints</label>
        <span class="value income">{{ totalPrints | number }}</span>
      </div>
      <div class="card summary-card highlight">
        <label>Total Amount (USD) <span class="rate-note" style="color: #94a3b8;">(Prints &times; $0.25)</span></label>
        <span class="value">{{ totalAmount | currency:'USD':'symbol':'1.2-2' }}</span>
      </div>
    </div>

    <div class="card main-card" style="padding: 0; overflow: hidden;">
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 50px;">Rank</th>
            <th *ngIf="reportType === 'device' || reportType === 'store'">Store ID</th>
            <th *ngIf="reportType === 'device'">Device ID</th>
            <th>Frame Group ID</th>
            <th>Frame ID</th>
            <th style="width: 70px;">Image</th>
            <th class="text-right" style="width: 100px;">Prints</th>
            <th class="text-right fee-col" style="width: 120px;">Amount ($0.25)</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of gridData">
            <td class="text-center font-bold">#{{ row.rank }}</td>
            <td *ngIf="reportType === 'device' || reportType === 'store'" class="text-center">{{ row.storeId }}</td>
            <td *ngIf="reportType === 'device'" class="text-center">{{ row.deviceId }}</td>
            <td class="text-center">{{ row.frameGroupId }}</td>
            <td class="text-center"><div class="frame-id">{{ row.frameId }}</div></td>
            <td class="text-center image-col">
              <img *ngIf="row.imageUrl" [src]="row.imageUrl" crossorigin="anonymous" referrerpolicy="no-referrer" alt="Frame" class="grid-img">
              <span *ngIf="!row.imageUrl" class="no-img">No Img</span>
            </td>
            <td class="text-right font-bold">{{ row.printCount | number }}</td>
            <td class="text-right font-bold fee-col">{{ ((row.printCount || 0) * 0.25) | currency:'USD':'symbol':'1.2-2' }}</td>
          </tr>
          <tr *ngIf="gridData.length === 0">
            <td colspan="8" class="empty-table-msg">No frame records found for the selected criteria.</td>
          </tr>
        </tbody>
        <tfoot *ngIf="gridData.length > 0">
          <tr>
            <th [attr.colspan]="(reportType === 'device' ? 5 : (reportType === 'store' ? 4 : 3))" class="text-center font-bold">Total</th>
            <th></th>
            <th class="text-right font-bold">{{ totalPrints | number }}</th>
            <th class="text-right font-bold fee-col">{{ totalAmount | currency:'USD':'symbol':'1.2-2' }}</th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</div>
